import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from '../components/common/ProtectedRoute';
import { ROLES } from '../utils/constants';

// Layout
import { AdminLayout } from '../layouts/AdminLayout';

// Auth
import { Login } from '../pages/auth/Login';
import { Register } from '../pages/auth/Register';

// Dashboard
import { SuperAdminDashboard } from '../pages/dashboard/SuperAdminDashboard';
import { VendorDashboard } from '../pages/dashboard/VendorDashboard';
import { ComplimentaryBills } from '../pages/dashboard/ComplimentaryBills';

// Vendor
import { VendorList } from '../pages/vendor/VendorList';
import { VendorCreate } from '../pages/vendor/VendorCreate';
import { VendorDetail } from '../pages/vendor/VendorDetail';

// Branch
import { BranchList } from '../pages/branch/BranchList';
// import { BranchDetails } from '../pages/branch/BranchDetails';

// Staff
import { StaffList } from '../pages/staff/StaffList';
import { StaffCreate } from '../pages/staff/StaffCreate';

// Products
import { Category } from '../pages/products/Category';
import { Product } from '../pages/products/Product';
import { Variant } from '../pages/products/Variant';

// Orders
import { OrderList } from '../pages/orders/OrderList';
import { OrderCreate } from '../pages/orders/OrderCreate';

// POS
import { POS } from '../pages/pos/POS';

// Settings
import { GSTSettings } from '../pages/settings/GSTSettings';

// Reports
import Reports from '../pages/reports/Reports';
import SalesSummary from '../pages/reports/SalesSummary';
import ItemsSummary from '../pages/reports/ItemsSummary';
import CategorySummary from '../pages/reports/CategorySummary';
import BillingTypeSummary from '../pages/reports/BillingTypeSummary';
import CaptainWiseSummary from '../pages/reports/CaptainWiseSummary';
import SalesReport from '../pages/reports/SalesReport';
import PaymentSummary from '../pages/reports/PaymentSummary';

// Menu & Users
import Menu from '../pages/Menu';
import MenuCategories from '../pages/MenuCategories';
import CategoryDishes from '../pages/CategoryDishes';
import Users from '../pages/Users';

import { RoleBasedRedirect } from '../components/common/RoleBasedRedirect';

export const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />

      {/* Super Admin Routes with Layout */}
      <Route
        path="/super-admin/*"
        element={
          <ProtectedRoute requiredRoles={[ROLES.SUPERADMIN]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<SuperAdminDashboard />} />
      </Route>

      {/* Vendor Routes with Layout */}
      <Route
        path="/vendor/*"
        element={
          <ProtectedRoute requiredRoles={[ROLES.VENDOR]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<VendorDashboard />} />
        <Route path="complimentary-bills" element={<ComplimentaryBills />} />
      </Route>

      {/* Super Admin Management Routes */}
      <Route
        path="/vendors/*"
        element={
          <ProtectedRoute requiredRoles={[ROLES.SUPERADMIN]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<VendorList />} />
        <Route path="create" element={<VendorCreate />} />
        <Route path=":vendorId" element={<VendorDetail />} />
      </Route>

      {/* Branch Routes */}
      <Route
        path="/branches/*"
        element={
          <ProtectedRoute requiredRoles={[ROLES.VENDOR]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<BranchList />} />
      </Route>

      {/* Staff Routes */}
      <Route
        path="/staff/*"
        element={
          <ProtectedRoute requiredRoles={[ROLES.SUPERADMIN, ROLES.VENDOR]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<StaffList />} />
        <Route path="create" element={<StaffCreate />} />
      </Route>

      {/* Products Routes */}
      <Route
        path="/products/*"
        element={
          <ProtectedRoute requiredRoles={[ROLES.VENDOR]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Product />} />
        <Route path="categories" element={<Category />} />
        <Route path="variants" element={<Variant />} />
      </Route>

      {/* Orders Routes */}
      <Route
        path="/orders/*"
        element={
          <ProtectedRoute requiredRoles={[ROLES.VENDOR, ROLES.BILLING, ROLES.CAPTAIN, ROLES.WAITER]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<OrderList />} />
        <Route path="create" element={<OrderCreate />} />
      </Route>

      {/* POS Route */}
      <Route
        path="/pos/*"
        element={
          <ProtectedRoute requiredRoles={[ROLES.BILLING, ROLES.CAPTAIN]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<POS />} />
      </Route>

      {/* Settings Routes */}
      <Route
        path="/settings/*"
        element={
          <ProtectedRoute requiredRoles={[ROLES.SUPERADMIN, ROLES.VENDOR]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<GSTSettings />} />
      </Route>

      {/* Reports Routes */}
      <Route
        path="/reports/*"
        element={
          <ProtectedRoute requiredRoles={[ROLES.VENDOR]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Reports />} />
        <Route path="sales-summary" element={<SalesSummary />} />
        <Route path="items-summary" element={<ItemsSummary />} />
        <Route path="category-summary" element={<CategorySummary />} />
        <Route path="billing-type-summary" element={<BillingTypeSummary />} />
        <Route path="captain-wise-summary" element={<CaptainWiseSummary />} />
        <Route path="sales-report" element={<SalesReport />} />
        <Route path="payment-summary" element={<PaymentSummary />} />
      </Route>

      {/* Menu & Users Routes */}
      <Route
        path="/dish-access/*"
        element={
          <ProtectedRoute requiredRoles={[ROLES.VENDOR]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Menu />} />
        <Route path=":menuId" element={<MenuCategories />} />
        <Route path=":menuId/categories/:categoryId" element={<CategoryDishes />} />
      </Route>

      <Route
        path="/users/*"
        element={
          <ProtectedRoute requiredRoles={[ROLES.VENDOR]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Users />} />
      </Route>

      {/* Default */}
      <Route path="/" element={<RoleBasedRedirect />} />
      <Route path="*" element={<RoleBasedRedirect />} />
    </Routes>
  );
};
